import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { capitalize } from "../../utils/manipulateText";

const DEFAULT_FONTS = [
  "System UI",
  "Arial",
  "Helvetica",
  "Georgia",
  "Times New Roman",
  "Trebuchet MS",
  "Verdana",
  "Tahoma",
  "Courier New",
  "Monaco",
  "Inter",
  "Roboto",
  "Open Sans",
  "Lato",
  "Poppins",
];

const FontSelect = ({
  name,
  label,
  value,
  onChange,
  fonts = DEFAULT_FONTS,
  className = "",
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = value || "";

  const handleSelect = (font) => {
    onChange?.({ target: { name, value: font } });
    setOpen(false);
  };

  const handleClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`mb-4 ${className}`} ref={ref}>
      {label && (
        <label
          className="block text-gray-700 text-sm font-bold mb-2"
          htmlFor={name}
        >
          {capitalize(t(label))}
        </label>
      )}
      <button
        id={name}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full bg-gray-200 focus:bg-white border border-gray-300 rounded px-3 py-2 text-left"
        style={{ fontFamily: selected || undefined }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected || capitalize(t("select"))}
      </button>
      {open && (
        <div className="relative">
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow-md h-64 overflow-auto">
            <div role="listbox" aria-labelledby={name} className="py-1">
              {fonts.map((font) => (
                <div
                  key={font}
                  role="option"
                  aria-selected={selected === font}
                  onClick={() => handleSelect(font)}
                  className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                    selected === font ? "bg-indigo-50 text-indigo-700" : ""
                  }`}
                  style={{ fontFamily: font }}
                >
                  {font}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

FontSelect.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  fonts: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
};

export default FontSelect;
